import { CalendarDays } from 'lucide-react';
import { useOrgBranding } from '@/hooks/useOrgBranding';
import { MetallicHeroBackdrop } from './MetallicHeroBackdrop';
import ziplinkLogo from '@/assets/ziplink-logo.png';

interface WelcomeHeroProps {
  firstName?: string;
  /**
   * Valfri hjältebild. När den saknas renderas den metalliska
   * smart lock-bakgrunden (MetallicHeroBackdrop) istället. Byt/mata in en
   * riktig bild-URL här (t.ex. från organisationens branding) för att visa foto.
   */
  imageUrl?: string | null;
}

/**
 * WelcomeHero — full-bleed glass hero for the home/landing screen.
 *
 * Layers, back to front:
 *   1) MetallicHeroBackdrop — nästan svart yta med subtila metallband
 *      (eller en hjältebild om sådan finns)
 *   2) mörk botten-vinjett så texten alltid har kontrast
 *   3) specular glass-sheen längs överkanten
 *   4) innehåll: ZipLink-logotypen (svartvit ordbild, vit på mörk yta) uppe
 *      till vänster, hälsning + datum nere
 *
 * Rent monokromt glas i hela appen — hjälten bär samma gråskala men får
 * djup av metallmönstret, vilket gör välkomstsidan distinkt utan att
 * bryta designspråket.
 */
export function WelcomeHero({ firstName, imageUrl }: WelcomeHeroProps) {
  const { orgName } = useOrgBranding();

  const today = new Date().toLocaleDateString('sv-SE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  return (
    <section
      className="relative overflow-hidden rounded-[28px] min-h-[248px] flex flex-col justify-between p-5 text-white animate-fade-in shadow-[0_20px_60px_-24px_hsl(var(--glass-shadow))]"
      style={
        imageUrl
          ? { backgroundImage: `url(${imageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }
          : undefined
      }
    >
      {!imageUrl && <MetallicHeroBackdrop />}

      {/* Bottom vignette — guarantees text contrast over any image/pattern */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.15) 45%, rgba(0,0,0,0.62) 100%)' }}
      />
      {/* Specular top sheen */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-24"
        style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0) 100%)' }}
      />

      {/* Top: ZipLink-logotypen uppe till vänster (svart ordbild → vit på mörk yta) */}
      <div className="relative flex items-center">
        <img
          src={ziplinkLogo}
          alt="ZipLink"
          className="h-6 w-auto max-w-[150px] object-contain"
          style={{ filter: 'brightness(0) invert(1)' }}
        />
      </div>

      {/* Bottom: greeting */}
      <div className="relative">
        <h1 className="text-[28px] font-semibold leading-tight tracking-tight">
          {firstName ? `Välkommen, ${firstName}` : 'Välkommen'}
        </h1>
        <p className="mt-1.5 flex items-center gap-1.5 text-sm text-white/75">
          <CalendarDays className="h-3.5 w-3.5 shrink-0" />
          <span className="first-letter:uppercase">{today}</span>
        </p>
      </div>
    </section>
  );
}
